import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import type { Overview, ActivityBand } from '@/api/types';
import { ActivityBadge } from '@/components/ActivityBadge';
import { PageHeader } from '@/components/AppShell';
import { ErrorState, cx } from '@/components/ui';
import { useResource } from '@/lib/hooks';
import { count, money } from '@/lib/format';

export function OverviewPage() {
  const { t, i18n } = useTranslation();
  const { data, error, loading, reload } = useResource<Overview>(
    '/api/v1/admin/overview',
    undefined,
    [],
  );

  if (error) return <ErrorState message={t('common.loadFailed')} onRetry={reload} />;

  const language = i18n.language;
  const bands: ActivityBand[] = ['active', 'lapsing', 'dormant', 'unknown'];
  const totalTracked = bands.reduce(
    (sum, band) => sum + (data?.activity[band] ?? 0),
    0,
  );

  return (
    <>
      <PageHeader title={t('overview.title')} />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Stat
          label={t('overview.athletes')}
          value={loading ? null : count(data?.athletes ?? 0, language)}
        />
        <Stat
          label={t('overview.coaches')}
          value={loading ? null : count(data?.coaches ?? 0, language)}
        />
        <Stat
          label={t('overview.clubs')}
          value={loading ? null : count(data?.clubs ?? 0, language)}
          note={
            loading
              ? undefined
              : `${count(data?.partner_clubs ?? 0, language)} ${t('overview.partnerClubs')}`
          }
        />
        {/* Athletes, coaches and clubs all count sign-ups, which say nothing
            about whether anybody came back. This one does. */}
        <Stat
          label={t('overview.recordings')}
          value={loading ? null : count(data?.recordings?.week ?? 0, language)}
          note={
            loading
              ? undefined
              : t('overview.recordingsMonth', {
                  count: data?.recordings?.month ?? 0,
                })
          }
        />
        <Stat
          label={t('overview.mrr')}
          value={loading ? null : money(data?.mrr_cents ?? 0, 'EUR', language)}
          note={
            loading
              ? undefined
              : t('overview.payingCount', { count: data?.paying_count ?? 0 })
          }
        />
      </div>

      <p className="mt-2 flex items-start gap-1.5 text-xs text-ink-subtle">
        <Info aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {t('overview.revenueCaveat')}
      </p>

      <section className="card mt-6 p-5">
        <h2 className="text-sm font-semibold text-ink">{t('overview.activityTitle')}</h2>
        <p className="mt-1 text-xs text-ink-subtle">{t('overview.activityHint')}</p>

        <div className="mt-4 space-y-3">
          {bands.map((band) => {
            const value = data?.activity[band] ?? 0;
            const share = totalTracked ? Math.round((value / totalTracked) * 100) : 0;
            return (
              <div key={band} className="flex items-center gap-3">
                <div className="w-28 shrink-0">
                  <ActivityBadge band={band} />
                </div>
                {/*
                  A bar plus the figure. The bar alone cannot be read exactly
                  and the figure alone cannot be compared at a glance.
                */}
                {/*
                  While the request is out the figures are still zero, and a
                  bar that announces "0 (0%)" reports an empty product rather
                  than one that has not answered yet. The column beside it
                  already shows a dash for exactly this reason.
                */}
                <div
                  className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-sunken"
                  role="img"
                  aria-label={
                    loading
                      ? `${t(`activity.${band}`)}: ${t('common.loading')}`
                      : `${t(`activity.${band}`)}: ${value} (${share}%)`
                  }
                >
                  <div
                    className={cx('h-full rounded-full', BAR[band])}
                    style={{ width: `${share}%` }}
                  />
                </div>
                <span className="w-20 shrink-0 text-right text-sm tabular text-ink">
                  {loading ? '—' : count(value, language)}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="card mt-4 p-5">
        <h2 className="text-sm font-semibold text-ink">
          {t('overview.subscriptionsTitle')}
        </h2>
        <dl className="mt-4 grid gap-3 sm:grid-cols-3">
          {Object.entries(data?.subscriptions ?? {})
            .filter(([, value]) => value > 0)
            .map(([status, value]) => (
              <div key={status} className="rounded-lg bg-surface-sunken px-3 py-2">
                <dt className="text-xs text-ink-subtle">{t(`subscription.${status}`)}</dt>
                <dd className="text-base font-semibold tabular text-ink">
                  {count(value, language)}
                </dd>
              </div>
            ))}
        </dl>
      </section>
    </>
  );
}

const BAR: Record<ActivityBand, string> = {
  active: 'bg-emerald-500',
  lapsing: 'bg-amber-500',
  dormant: 'bg-rose-500',
  unknown: 'bg-slate-400',
};

function Stat({
  label,
  value,
  note,
}: {
  label: string;
  value: string | null;
  note?: string;
}) {
  return (
    <div className="card p-4">
      <p className="text-xs text-ink-subtle">{label}</p>
      {value === null ? (
        // Reserves the line the figure will occupy, so the card does not
        // change height when the data lands.
        <div className="mt-1 h-8 w-24 animate-pulse rounded bg-surface-sunken" />
      ) : (
        <p className="mt-1 text-2xl font-semibold tabular tracking-tight text-ink">
          {value}
        </p>
      )}
      {note ? <p className="mt-1 text-xs text-ink-subtle">{note}</p> : null}
    </div>
  );
}
